import {
  FluxAccountType,
  FluxCurrentSituation,
} from '@energy-retailer/constants';
import { z } from 'zod';
import { evDetailsSchema } from '../ev-details-schema';
import { fluxAddressSchema } from '../flux-address-schema';
import { hazardzSchema } from '../hazards-schema';
import { partySchema } from '../party-schema';
import {
  electricityConnectionSchema,
  gasConnectionSchema,
} from '../utility-details-schema';
import { maxStartDate, minStartDate } from '../utils';

const requestedSupplyDateSchema = z.union([
  z
    .date({
      required_error: 'Date is required',
      invalid_type_error: 'Please enter a valid date',
    })
    .min(minStartDate, 'Must be today onwards')
    .max(maxStartDate, 'No greater than two months in the future'),
  z.string().length(0),
]);

export const detailsSchema = z.object({
  connectionInformation: z
    .object({
      currentSituation: z
        .nativeEnum(FluxCurrentSituation)
        .or(z.string())
        .optional()
        .superRefine((value, ctx) => {
          if (value === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Please select an option',
            });
            return false;
          }
          return true;
        })
        .transform((value) => {
          if (value === 'addFuel') {
            // Note: Treat this option the same as adding
            // a property to the account. Flux will have
            // to handle overlaps on dual fuel/single gas
            return FluxCurrentSituation.Existing;
          }

          return value;
        }),
      requestedSupplyDate: requestedSupplyDateSchema,
      isExistingMember: z
        .boolean()
        .optional()
        .superRefine((value, ctx) => {
          if (value === undefined) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Please select an option',
            });
            return false;
          }
          return true;
        }),
      isEvEligibleCustomer: z.boolean().optional(),
      ownElectricVehicle: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (
        value.isExistingMember &&
        value.currentSituation !== FluxCurrentSituation.Existing &&
        (!value.requestedSupplyDate || value.requestedSupplyDate === '')
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Date is required',
          path: ['requestedSupplyDate'],
        });
      }

      if (value.isEvEligibleCustomer) {
        if (!value.ownElectricVehicle) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Please check you own electric vehicle',
            path: ['ownElectricVehicle'],
          });
          return false;
        }
        return true;
      }
      return true;
    }),

  address: fluxAddressSchema,
  sensitiveLoad: z.enum(['yes', 'no'], {
    errorMap: () => {
      return { message: 'Please select an option' };
    },
  }),
  accountType: z
    .nativeEnum(FluxAccountType)
    .default(FluxAccountType.Residential),
  parties: partySchema,
  connection: z.object({
    electricityConnection: electricityConnectionSchema.optional(),
    gasConnection: gasConnectionSchema.optional(),
  }),
  helpToGetConnected: z.boolean(),
  hazards: hazardzSchema,
  evDetails: evDetailsSchema.optional(),
  referralCode: z.string().optional(),
  products: z.object({
    electricityFluxId: z.string().optional().nullable(),
    gasFluxId: z.string().optional().nullable(),
  }),
});

export type DetailsSchema = z.infer<typeof detailsSchema>;
